import { useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowSquareOut,
  CheckCircle,
  CreditCard,
  Gear,
  HourglassMedium,
  Plugs,
  PlugsConnected,
  Storefront,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  selectClinicProfile,
  selectIsAdmin,
  selectPlanAllowsGrow,
} from "../../store/slices/authSlice";
import { formatDateTime, formatHebrewDate } from "../../utils/dateUtils";
import GrowSetupWizard from "./GrowSetupWizard";
import GrowSettingsModal from "./GrowSettingsModal";
import GrowDisconnectModal from "./GrowDisconnectModal";
import GrowPartnerOnboarding from "./GrowPartnerOnboarding";
import { GROW_BACKOFFICE_URL, growMethodLabel } from "./growUi";
import "./GrowIntegration.css";

/**
 * GrowIntegrationCard — Settings → אינטגרציות card for Grow payments.
 * States: plan blocked · not connected · partner pending · connected.
 * All writes go through callables (wizard / settings / disconnect modals).
 */
export default function GrowIntegrationCard() {
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const planAllows = useSelector(selectPlanAllowsGrow);

  const [showWizard, setShowWizard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [showPartner, setShowPartner] = useState(false);

  const integrated = clinicProfile?.isGrowIntegrated === true;
  const gi = clinicProfile?.growIntegration || {};
  const partnerPending = !integrated && gi.partnerStatus === "pending";
  const partnerFlowEnabled = gi.partnerFlowEnabled === true;
  const isSandbox = gi.environment === "sandbox";
  const hasError = !!gi.lastError;

  return (
    <>
      <section className="grow-card" aria-labelledby="grow-card-title">
        <header className="grow-card-head">
          <span className="grow-card-logo"><CreditCard size={22} weight="fill" /></span>
          <div className="grow-card-head-text">
            <div className="grow-card-title-row">
              <h3 id="grow-card-title">Grow תשלומים</h3>
              {integrated ? (
                <span className="grow-badge ok"><PlugsConnected size={13} weight="bold" />מחובר</span>
              ) : partnerPending ? (
                <span className="grow-badge pending"><HourglassMedium size={13} weight="bold" />ממתין לאישור Grow</span>
              ) : (
                <span className="grow-badge muted"><Plugs size={13} weight="bold" />לא מחובר</span>
              )}
              {integrated && isSandbox && <span className="grow-badge sandbox">סביבת בדיקות</span>}
            </div>
            <p>דרישות תשלום למטופלים בוואטסאפ — אשראי, Bit, Apple Pay ו-Google Pay. התשלום מתעדכן לבד בפיננסים.</p>
          </div>
        </header>

        <div className="grow-card-body">
          {!planAllows ? (
            <div className="grow-callout warn">
              <WarningCircle size={18} weight="fill" />
              <span>סליקה דרך Grow אינה כלולה במסלול הנוכחי. פנו אלינו לשדרוג.</span>
            </div>
          ) : integrated ? (
            <>
              <dl className="grow-info-grid">
                <div>
                  <dt>מזהה עסק</dt>
                  <dd dir="ltr">{gi.userIdHash || "—"}</dd>
                </div>
                <div>
                  <dt>מחובר מתאריך</dt>
                  <dd>{gi.connectedAt ? formatHebrewDate(gi.connectedAt) : "—"}</dd>
                </div>
                <div>
                  <dt>אמצעי תשלום</dt>
                  <dd className="grow-chips">
                    {(gi.methods || []).map((m) => (
                      <span key={m} className="grow-chip">{growMethodLabel(m)}</span>
                    ))}
                  </dd>
                </div>
                <div>
                  <dt>שליחה אוטומטית</dt>
                  <dd>{gi.autoSendOnCompletion ? `פעילה${gi.autoSendDelayMinutes ? ` (עיכוב ${gi.autoSendDelayMinutes} דק')` : ""}` : "כבויה"}</dd>
                </div>
                <div>
                  <dt>עדכון אחרון מ-Grow</dt>
                  <dd>{gi.lastCallbackAt ? formatDateTime(gi.lastCallbackAt) : "עדיין לא התקבל"}</dd>
                </div>
                <div>
                  <dt>קבלות</dt>
                  <dd>{gi.invoiceMode === "none" ? "ללא" : "אוטומטי מ-Grow"}</dd>
                </div>
              </dl>

              {hasError && (
                <div className="grow-callout warn">
                  <WarningCircle size={18} weight="fill" />
                  <span>הקריאה האחרונה ל-Grow נכשלה (שגיאה {gi.lastError.growErrorId ?? "—"}). בדקו שהמזהים עדיין תקפים.</span>
                </div>
              )}

              {isAdmin && (
                <div className="grow-card-actions">
                  <button type="button" className="grow-btn secondary" onClick={() => setShowSettings(true)}>
                    <Gear size={16} /> הגדרות
                  </button>
                  <a className="grow-btn ghost" href={GROW_BACKOFFICE_URL} target="_blank" rel="noreferrer">
                    <ArrowSquareOut size={16} /> כניסה ל-Grow
                  </a>
                  <button type="button" className="grow-btn ghost danger-text" onClick={() => setShowDisconnect(true)}>
                    <Plugs size={16} /> ניתוק
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <ul className="grow-benefits">
                <li><CheckCircle size={16} weight="fill" />קישור תשלום לכל פגישה — נשלח בוואטסאפ או ב-SMS</li>
                <li><CheckCircle size={16} weight="fill" />הסטטוס בפיננסים מתעדכן אוטומטית כשהמטופל משלם</li>
                <li><CheckCircle size={16} weight="fill" />קבלה אוטומטית נשמרת על הפגישה</li>
                <li><CheckCircle size={16} weight="fill" />תזכורת למטופל אם לא שולם</li>
              </ul>

              {partnerPending ? (
                <div className="grow-callout">
                  <HourglassMedium size={18} weight="fill" />
                  <span>בקשת פתיחת החשבון נשלחה ל-Grow. נעדכן ברגע שהחשבון יאושר.</span>
                </div>
              ) : isAdmin ? (
                <div className="grow-card-actions">
                  <button type="button" className="grow-btn primary" onClick={() => setShowWizard(true)}>
                    <Plugs size={16} weight="bold" /> יש לי חשבון Grow
                  </button>
                  {partnerFlowEnabled ? (
                    <button type="button" className="grow-btn secondary" onClick={() => setShowPartner(true)}>
                      <Storefront size={16} /> פתיחת חשבון Grow
                    </button>
                  ) : (
                    <span className="grow-badge muted" title="פתיחת חשבון Grow מתוך CliniKit — בקרוב">פתיחת חשבון דרך CliniKit — בקרוב</span>
                  )}
                </div>
              ) : (
                <p className="grow-note">רק מנהל/ת הקליניקה יכול/ה לחבר את Grow.</p>
              )}
            </>
          )}
        </div>
      </section>

      <GrowSetupWizard isOpen={showWizard} onClose={() => setShowWizard(false)} />
      <GrowSettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
      <GrowDisconnectModal isOpen={showDisconnect} onClose={() => setShowDisconnect(false)} />
      <GrowPartnerOnboarding isOpen={showPartner} onClose={() => setShowPartner(false)} partnerStatus={gi.partnerStatus || null} />
    </>
  );
}
